import React from 'react';
import { STATUS } from '../utils/careerLogic';

const ProgressStats = ({ materias, orientacionSeleccionada, cpoPuntos }) => {
  // Usamos directamente las materias pasadas por props, que son la fuente de verdad de App.js
  const materiasInput = materias || {};

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const stats = {
      cbc: { cursadas: 0, finales: 0, total: 6 },
      cpc: { cursadas: 0, finales: 0, total: 14 },
      cpo: { cursadas: 0, finales: 0, total: 0 },
      general: { cursadas: 0, finales: 0, total: 0 }
    };

    Object.values(materiasInput).forEach(materia => {
      const estaAprobada = materia.estado === STATUS.CURSADA_APROBADA || materia.notaFinal === -3;
      
      if (materia.categoria === 'CBC') {
        if (estaAprobada) stats.cbc.cursadas++;
        // FINAL_APROBADO eliminado; no se contabiliza por separado
      } else if (materia.categoria === 'CPC') {
        if (estaAprobada) stats.cpc.cursadas++;
        // FINAL_APROBADO eliminado; no se contabiliza por separado
      } else if (materia.categoria?.startsWith('CPO')) {
        stats.cpo.total++;
        if (estaAprobada) stats.cpo.cursadas++;
        // FINAL_APROBADO eliminado; no se contabiliza por separado
      }
    });

    stats.general.cursadas = stats.cbc.cursadas + stats.cpc.cursadas + stats.cpo.cursadas;
    stats.general.finales = stats.cbc.finales + stats.cpc.finales + stats.cpo.finales;
    stats.general.total = stats.cbc.total + stats.cpc.total + stats.cpo.total;

    return stats;
  };

  const stats = calcularEstadisticas();

  const ProgressBar = ({ current, total, color = 'var(--accent-color)' }) => {
    const percentage = total > 0 ? (current / total) * 100 : 0;
    return (
      <div style={{
        width: '100%',
        height: '8px',
        backgroundColor: 'var(--border-color)',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.3s ease'
        }} />
      </div>
    );
  };

  const StatCard = ({ title, cursadas, finales, total, color, emoji }) => (
    <div style={{
      backgroundColor: 'var(--bg-secondary)',
      padding: '15px',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      boxShadow: 'var(--shadow-small)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: '1.2em', marginRight: '8px' }}>{emoji}</span>
        <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>{title}</h4>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>Cursadas</span>
          <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{cursadas}/{total}</span>
        </div>
        <ProgressBar current={cursadas} total={total} color={color} />
      </div>

      {finales > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>Finales</span>
            <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{finales}/{total}</span>
          </div>
          <ProgressBar current={finales} total={total} color={color} />
        </div>
      )}

      <div style={{ 
        fontSize: '0.8em', 
        color: 'var(--text-secondary)', 
        marginTop: '8px',
        textAlign: 'center'
      }}>
        {cursadas === total ? '🎉 ¡Completado!' : `Faltan ${total - cursadas} materias`}
      </div>
    </div>
  );

  // Normalizar cpoPuntos para manejar tanto número como objeto
  const puntosTotal = typeof cpoPuntos === 'number' ? cpoPuntos : (cpoPuntos?.total || 0);

  // Calcular progreso general de la carrera
  // Para ser abogado necesitas: CBC (6) + CPC (14) + CPO (69 puntos)
  const esCBCCompleto = stats.cbc.cursadas === stats.cbc.total;
  const esCPCCompleto = stats.cpc.cursadas === stats.cpc.total;
  const esCPOCompleto = puntosTotal >= 69;
  
  // Cálculo de progreso ponderado
  const cbcPorcentaje = stats.cbc.total > 0 ? (stats.cbc.cursadas / stats.cbc.total) : 0;
  const cpcPorcentaje = stats.cpc.total > 0 ? (stats.cpc.cursadas / stats.cpc.total) : 0;
  const cpoPorcentaje = Math.min(puntosTotal / 69, 1);
  
  // Peso de cada ciclo (CBC: 20%, CPC: 30%, CPO: 50%)
  const porcentajeCarrera = Math.round(
    (cbcPorcentaje * 20) + (cpcPorcentaje * 30) + (cpoPorcentaje * 50)
  );

  const getMensajeMotivacional = () => {
    if (esCBCCompleto && esCPCCompleto && esCPOCompleto) {
      return "🎉 ¡FELICITACIONES, ABOGADO/A! 👨‍⚖️👩‍⚖️";
    }
    if (porcentajeCarrera === 0) return "¡Bienvenido a tu carrera de Derecho! 🎓";
    if (porcentajeCarrera < 25) return "¡Excelente inicio! Seguí así 💪";
    if (porcentajeCarrera < 50) return "¡Vas por buen camino! 🚀";
    if (porcentajeCarrera < 75) return "¡Ya estás en la recta final! 🏃‍♂️";
    if (esCBCCompleto && esCPCCompleto && !esCPOCompleto) {
      return "¡Falta poco! Completá los 69 puntos CPO 🎯";
    }
    return "¡Casi abogado/a! ¡No aflojes! 🎯";
  };

  return (
    <div style={{
      margin: '0',
      padding: '10px',
      backgroundColor: 'transparent',
      borderRadius: '8px',
      border: 'none',
      boxShadow: 'none'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h3 style={{ 
          color: 'var(--accent-color)', 
          margin: '0 0 10px 0',
          fontSize: '1.3em'
        }}>
          📊 Tu Progreso en la Carrera
        </h3>
        <div style={{
          fontSize: '2em',
          fontWeight: 'bold',
          color: 'var(--accent-color)',
          marginBottom: '5px'
        }}>
          {porcentajeCarrera}%
        </div>
        <p style={{ 
          margin: 0, 
          color: 'var(--text-secondary)',
          fontStyle: 'italic',
          fontSize: '1.1em'
        }}>
          {getMensajeMotivacional()}
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <ProgressBar 
          current={porcentajeCarrera} 
          total={100} 
          color="var(--accent-color)" 
        />
        <div style={{ 
          textAlign: 'center', 
          marginTop: '5px', 
          fontSize: '0.9em',
          color: 'var(--text-secondary)'
        }}>
          Progreso total: CBC {esCBCCompleto ? '✅' : '⏳'} + CPC {esCPCCompleto ? '✅' : '⏳'} + CPO {esCPOCompleto ? '✅' : `${puntosTotal}/69 pts`}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
        marginBottom: '20px'
      }}>
        <StatCard
          title="CBC"
          cursadas={stats.cbc.cursadas}
          finales={stats.cbc.finales}
          total={stats.cbc.total}
          color="var(--error-color)"
          emoji="📚"
        />
        
        <StatCard
          title="CPC"
          cursadas={stats.cpc.cursadas}
          finales={stats.cpc.finales}
          total={stats.cpc.total}
          color="var(--accent-color)"
          emoji="📖"
        />

        {orientacionSeleccionada && (
          <StatCard
            title={`CPO - ${orientacionSeleccionada.nombre}`}
            cursadas={stats.cpo.cursadas}
            finales={stats.cpo.finales}
            total={stats.cpo.total}
            color="var(--success-color)"
            emoji="🎯"
          />
        )}
      </div>

      {puntosTotal > 0 && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <h4 style={{ 
            color: 'var(--accent-color)', 
            margin: '0 0 10px 0',
            textAlign: 'center'
          }}>
            🏆 Puntos CPO
          </h4>
          <div style={{ textAlign: 'center' }}>
            <span style={{ 
              fontSize: '1.5em', 
              fontWeight: 'bold',
              color: 'var(--success-color)'
            }}>
              {puntosTotal}/69 puntos
            </span>
            <div style={{ marginTop: '5px' }}>
              <ProgressBar 
                current={puntosTotal} 
                total={69} 
                color="var(--success-color)" 
              />
            </div>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '15px',
        padding: '10px',
        backgroundColor: 'var(--bg-hover)',
        borderRadius: '6px',
        fontSize: '0.8em',
        color: 'var(--text-secondary)',
        textAlign: 'center'
      }}>
        💡 Tip: Hacé click en las materias para cambiar su estado y ver las correlativas
      </div>
    </div>
  );
};

export default ProgressStats; 